import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { BadgePercent, FolderTree, Receipt } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { classesQuery, yearsQuery } from '@/features/academic/api'
import { categoriesQuery, useSaveCategory, useSaveDiscount, useSaveFeeItem } from '../api'
import {
  categorySchema,
  discountSchema,
  feeItemSchema,
  type CategoryValues,
  type DiscountValues,
  type FeeItemValues,
} from '../schemas'
import { frequencyLabel } from '../status'
import { FEE_FREQUENCIES, type FeeCategory, type FeeDiscount, type FeeItem } from '../types'
import { FormDialog } from './FormDialog'
import { ToggleField } from './ToggleField'

interface DialogProps<T> {
  open: boolean
  onOpenChange: (open: boolean) => void
  record: T | null
}

export function CategoryDialog({ open, onOpenChange, record }: DialogProps<FeeCategory>) {
  const { t } = useTranslation('finance')
  const save = useSaveCategory()
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CategoryValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: { name: record?.name ?? '', isActive: record?.isActive ?? true },
  })

  const submit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ ...v, id: record?.id })
      toast.success(record ? t('Category updated') : t('Category created'), v.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, ['name', 'isActive'])
    }
  })

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<FolderTree />}
      title={record ? t('Edit category') : t('New fee category')}
      description={t('Categories group fee items on invoices and reports (e.g. Tuition, Transport).')}
      formId="fee-category-form"
      submitLabel={record ? t('Save changes') : t('Create category')}
      pending={save.isPending}
      size="sm"
    >
      <form id="fee-category-form" onSubmit={(e) => void submit(e)} noValidate className="grid gap-4">
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input autoComplete="off" placeholder={t('Tuition')} {...register('name')} />
        </FormField>
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <ToggleField
              label={t('Active')}
              description={t('Inactive categories stay on past invoices.')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </form>
    </FormDialog>
  )
}

const ITEM_FIELDS = [
  'categoryId',
  'academicYearId',
  'classId',
  'name',
  'amount',
  'frequency',
  'dueDay',
  'isMandatory',
  'isActive',
] as const

export function FeeItemDialog({
  open,
  onOpenChange,
  record,
  defaultYearId,
}: DialogProps<FeeItem> & { defaultYearId?: string }) {
  const { t } = useTranslation('finance')
  const save = useSaveFeeItem()
  const categories = useQuery(categoriesQuery())
  const years = useQuery(yearsQuery())
  const classes = useQuery(classesQuery())
  const currentYear = years.data?.find((y) => y.isCurrent)
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FeeItemValues>({
    resolver: zodResolver(feeItemSchema),
    defaultValues: {
      categoryId: record ? String(record.category.id) : '',
      academicYearId: record ? String(record.academicYear.id) : (defaultYearId ?? ''),
      classId: record?.schoolClass ? String(record.schoolClass.id) : '',
      name: record?.name ?? '',
      amount: record?.amount ?? '',
      frequency: record?.frequency ?? 'monthly',
      dueDay: record?.dueDay ? String(record.dueDay) : '',
      isMandatory: record?.isMandatory ?? true,
      isActive: record?.isActive ?? true,
    },
  })

  const submit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        id: record?.id,
        categoryId: Number(v.categoryId),
        academicYearId: Number(v.academicYearId || currentYear?.id),
        classId: v.classId ? Number(v.classId) : null,
        name: v.name,
        amount: v.amount,
        frequency: v.frequency,
        dueDay: v.dueDay ? Number(v.dueDay) : null,
        isMandatory: v.isMandatory,
        isActive: v.isActive,
      })
      toast.success(record ? t('Fee item updated') : t('Fee item created'), v.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, ITEM_FIELDS)
    }
  })

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Receipt />}
      title={record ? t('Edit fee item') : t('New fee item')}
      description={
        record && record.invoicedCount > 0
          ? t('Changes apply to future invoices only; issued invoices keep their original amounts.')
          : t('How often an item is billed decides which invoice runs include it.')
      }
      formId="fee-item-form"
      submitLabel={record ? t('Save changes') : t('Create item')}
      pending={save.isPending}
      size="lg"
    >
      <form
        id="fee-item-form"
        onSubmit={(e) => void submit(e)}
        noValidate
        className="grid gap-4 sm:grid-cols-2"
      >
        <FormField label={t('Name')} error={errors.name?.message} required className="sm:col-span-2">
          <Input autoComplete="off" placeholder={t('Tuition – Grade 5')} {...register('name')} />
        </FormField>
        <FormField label={t('Category')} error={errors.categoryId?.message} required>
          <Select
            placeholder={t('Choose…')}
            options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            {...register('categoryId')}
          />
        </FormField>
        <FormField label={t('Academic year')} error={errors.academicYearId?.message} required>
          <Select
            placeholder={t('Choose…')}
            options={(years.data ?? []).map((y) => ({
              value: y.id,
              label: y.isCurrent ? t('{{name}} (current)', { name: y.name }) : y.name,
            }))}
            {...register('academicYearId')}
          />
        </FormField>
        <FormField
          label={t('Class')}
          error={errors.classId?.message}
          hint={t('Leave on “All classes” for school-wide fees.')}
        >
          <Select
            placeholder={t('All classes')}
            options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            {...register('classId')}
          />
        </FormField>
        <FormField label={t('Amount')} error={errors.amount?.message} required>
          <Input inputMode="decimal" autoComplete="off" placeholder="150.00" {...register('amount')} />
        </FormField>
        <FormField
          label={t('Frequency')}
          error={errors.frequency?.message}
          required
          hint={t(
            'Monthly: every month run · Per term: first month of each 4-month term · Yearly / one-time: the annual run.',
          )}
        >
          <Select
            options={FEE_FREQUENCIES.map((f) => ({ value: f, label: t(frequencyLabel[f]) }))}
            {...register('frequency')}
          />
        </FormField>
        <FormField
          label={t('Due day')}
          error={errors.dueDay?.message}
          hint={t('Day of the month (1–28) invoices fall due.')}
        >
          <Input inputMode="numeric" placeholder="10" {...register('dueDay')} />
        </FormField>
        <Controller
          control={control}
          name="isMandatory"
          render={({ field }) => (
            <ToggleField
              className="rounded-control border border-line bg-surface-muted/60 p-3"
              label={t('Mandatory')}
              description={t('Optional items are billed only when a run includes them.')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <ToggleField
              className="rounded-control border border-line bg-surface-muted/60 p-3"
              label={t('Active')}
              description={t('Inactive items are never billed.')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </form>
    </FormDialog>
  )
}

export function DiscountDialog({ open, onOpenChange, record }: DialogProps<FeeDiscount>) {
  const { t } = useTranslation('finance')
  const save = useSaveDiscount()
  const {
    register,
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<DiscountValues>({
    resolver: zodResolver(discountSchema),
    defaultValues: {
      name: record?.name ?? '',
      type: record?.type ?? 'percentage',
      value: record?.value ?? '',
      maxAmount: record?.maxAmount ?? '',
      isActive: record?.isActive ?? true,
    },
  })
  const type = watch('type')

  const submit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        id: record?.id,
        ...v,
        maxAmount: v.type === 'percentage' && v.maxAmount ? v.maxAmount : null,
      })
      toast.success(record ? t('Discount updated') : t('Discount created'), v.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, ['name', 'type', 'value', 'maxAmount', 'isActive'])
    }
  })

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<BadgePercent />}
      title={record ? t('Edit discount') : t('New discount')}
      description={t(
        'Assign a discount to a student from their ledger; it applies to invoices generated afterwards.',
      )}
      formId="fee-discount-form"
      submitLabel={record ? t('Save changes') : t('Create discount')}
      pending={save.isPending}
    >
      <form
        id="fee-discount-form"
        onSubmit={(e) => void submit(e)}
        noValidate
        className="grid gap-4 sm:grid-cols-2"
      >
        <FormField label={t('Name')} error={errors.name?.message} required className="sm:col-span-2">
          <Input autoComplete="off" placeholder={t('Sibling discount')} {...register('name')} />
        </FormField>
        <div className="sm:col-span-2">
          <p id="discount-type-label" className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">
            {t('Type')}
          </p>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioGroup
                aria-labelledby="discount-type-label"
                appearance="cards"
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  {
                    value: 'percentage',
                    label: t('Percentage'),
                    description: t('A share of every line, optionally capped per invoice.'),
                  },
                  {
                    value: 'fixed',
                    label: t('Fixed amount'),
                    description: t('Taken once per invoice, never more than the invoice.'),
                  },
                ]}
              />
            )}
          />
        </div>
        <FormField
          label={type === 'percentage' ? t('Percentage') : t('Amount')}
          error={errors.value?.message}
          required
        >
          <Input
            inputMode="decimal"
            autoComplete="off"
            placeholder={type === 'percentage' ? '10' : '50.00'}
            {...register('value')}
          />
        </FormField>
        {type === 'percentage' ? (
          <FormField
            label={t('Cap per invoice')}
            error={errors.maxAmount?.message}
            hint={t('Optional maximum discount amount.')}
          >
            <Input inputMode="decimal" autoComplete="off" {...register('maxAmount')} />
          </FormField>
        ) : null}
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <ToggleField
              className="sm:col-span-2"
              label={t('Active')}
              description={t('Inactive discounts are skipped during generation.')}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
      </form>
    </FormDialog>
  )
}
